import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

class InvoiceGenerator {
  /**
   * Generate invoice PDF
   * @param {Object} invoiceData - Invoice details
   * @returns {Blob} - PDF blob
   */
  generateInvoicePDF(invoiceData) {
    const {
      invoiceNumber = 'N/A',
      invoiceDate = new Date().toLocaleDateString(),
      dueDate = '-',
      customerName = 'Walk-in Customer',
      customerPhone = '',
      customerEmail = '',
      customerAddress = '',
      shippingAddress = '-',
      items = [],
      payments = [],
      subtotal = 0,
      tax = 0,
      discount = 0,
      total = 0,
      totalPaid = 0,
      amountDue = 0,
      paymentMethod = 'Cash',
      businessName = "Lefty's Liquor",
      businessAddress = '14199 E HIGHWAY 12 ROGERS,, Little Rock, AR, 72756-8, USA',
      businessPhone = '',
      businessEmail = '',
      taxId = 'PAN12345K',
      invoicedBy = 'Admin',
      notes = '',
      isVoided = false
    } = invoiceData;

    const isReceipt = isVoided; 
    const doc = isReceipt 
      ? new jsPDF({ orientation: 'p', unit: 'mm', format: [80, 150], putOnlyUsedFonts: true })
      : new jsPDF();

    doc.setFont('helvetica');

    if (isReceipt) {
      // Keep thermal receipt logic for voided/POS
      // ... (existing thermal logic or simplified)
      this._renderThermalReceipt(doc, invoiceData);
      return doc;
    }

    // --- HIGH FIDELITY INVOICE DESIGN (Creative Souls Style) ---
    
    // Top Left: Business Branding
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'bold');
    doc.text(businessName.toUpperCase(), 10, 20);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const busAddrLines = doc.splitTextToSize(businessAddress, 70);
    doc.text(busAddrLines, 10, 25);

    // Center Branding / Logo
    const logoX = 85;
    const logoY = 12;
    const logoW = 40;
    const logoH = 25;

    try {
      // Try to load the brand logo from assets
      doc.addImage('/logo.png', 'PNG', logoX, logoY, logoW, logoH);
    } catch (e) {
      // High-Fidelity Stylized Fallback (Creative Souls CS Branding)
      doc.setFillColor(6, 182, 212); // Teal/Cyan brand color
      doc.circle(logoX + 15, logoY + 12, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('CS', logoX + 15, logoY + 16, { align: 'center' });
      
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('CREATIVE SOULS', logoX + (logoW / 2), logoY + 24, { align: 'center' });
    }

    // Top Right: Invoice Label & Serial Data
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text('INVOICE :', 210 - 10, 25, { align: 'right' });
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Date: ${invoiceDate}`, 210 - 10, 35, { align: 'right' });
    doc.text(`Due Date: ${dueDate}`, 210 - 10, 42, { align: 'right' });

    // Horizontal Spacing
    doc.setDrawColor(240, 240, 240);
    doc.line(10, 50, 200, 50);

    // SOLD TO / SHIP TO Section
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text('SOLD TO:', 10, 60);
    doc.text('SHIP TO:', 105, 60);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    
    // Sold To Details
    doc.text(customerName, 10, 67);
    const custAddrLines = doc.splitTextToSize(customerAddress || 'N/A', 80);
    doc.text(custAddrLines, 10, 72);
    let custY = 72 + (custAddrLines.length * 4);
    if (customerPhone) doc.text(`Mobile: ${customerPhone}`, 10, custY);
    doc.setFont('helvetica', 'bold');
    doc.text(`Phone: ${customerPhone || '--'}`, 10, custY + 5);

    // Ship To Details
    doc.setFont('helvetica', 'normal');
    doc.text(shippingAddress, 105, 67);
    doc.setFont('helvetica', 'bold');
    doc.text(`Phone: -`, 105, 75);

    // --- PAYMENT INFO TABLE ---
    const payTableData = payments.length > 0 ? payments.map(p => [
      p.method || 'Cash',
      `$${parseFloat(p.amount || 0).toFixed(2)}`,
      p.date || invoiceDate,
      p.cashier || invoicedBy,
      p.taxId || taxId
    ]) : [[paymentMethod || 'Cash', `$${parseFloat(total || 0).toFixed(2)}`, invoiceDate, invoicedBy, taxId]];

    autoTable(doc, {
      startY: 100,
      head: [['Payment Method', 'Amount', 'Payment Date', 'Invoiced By', 'Tax ID/Hemp']],
      body: payTableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [245, 245, 245], textColor: [60, 60, 60], fontStyle: 'bold', lineWidth: 0.1 },
      margin: { left: 10, right: 10 }
    });

    // --- PRODUCTS TABLE ---
    const prodTableData = items.map((item, i) => [
      i + 1,
      item.sku || '-',
      item.name,
      `${parseFloat(item.quantity || 0).toFixed(2)} Single`,
      parseFloat(item.unitPrice || 0).toFixed(2),
      parseFloat(item.total || 0).toFixed(2)
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 5,
      head: [['#', 'UPS / SKU', 'Product', 'Quantity', 'Unit Price', 'Subtotal']],
      body: prodTableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [245, 245, 245], textColor: [60, 60, 60], fontStyle: 'bold', lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 30 },
        2: { cellWidth: 90 },
        3: { cellWidth: 20, halign: 'right' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 20, halign: 'right' }
      },
      margin: { left: 10, right: 10 }
    });

    // --- TOTALS ---
    let finalY = doc.lastAutoTable.finalY + 10;
    const totalsX = 160;

    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    
    doc.text('Subtotal', totalsX, finalY, { align: 'right' });
    doc.text(`${parseFloat(subtotal || 0).toFixed(2)}`, 200, finalY, { align: 'right' });

    finalY += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Grand Total', totalsX, finalY, { align: 'right' });
    doc.text(`$ ${parseFloat(total || 0).toFixed(2)}`, 200, finalY, { align: 'right' });

    finalY += 5;
    doc.text('Total Paid', totalsX, finalY, { align: 'right' });
    doc.text(`$ ${parseFloat(totalPaid || total || 0).toFixed(2)}`, 200, finalY, { align: 'right' });

    finalY += 5;
    doc.text('Due Amount', totalsX, finalY, { align: 'right' });
    doc.text(`${parseFloat(amountDue || 0).toFixed(2)}`, 200, finalY, { align: 'right' });

    // --- FOOTER & QR CODE ---
    finalY += 15;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Return Policy:', 10, finalY);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    const policy = 'NOTE: ANY DISCREPANCIES OR SHORTAGE MUST BE NOTIFIED WITHIN 48 HOURS OF DELIVERY.\nBUYER IS RESPONSIBLE FOR APPLICABLE STATE TAXES.';
    doc.text(policy, 10, finalY + 4);

    // QR Code - use a public API to generate a real QR code
    const qrSize = 40;
    const qrX = (210 - qrSize) / 2;
    const qrY = finalY + 15;
    
    // ZATCA / Standard QR data
    const qrData = btoa(`Seller:${businessName}|VAT:${taxId}|Time:${invoiceDate}|Total:${total}`);
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`;
    
    try {
      doc.addImage(qrApiUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    } catch (e) {
      // Fallback if URL addImage fails (CORS or version)
      doc.setDrawColor(200, 200, 200);
      doc.rect(qrX, qrY, qrSize, qrSize);
      doc.setFontSize(7);
      doc.text('[ QR CODE ]', 105, qrY + (qrSize / 2), { align: 'center' });
    }

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('"Thank You For Your Business!"', 105, qrY + qrSize + 10, { align: 'center' });

    return doc;
  }

  // Internal helper for thermal receipts
  _renderThermalReceipt(doc, invoiceData) {
    const {
      invoiceNumber = 'N/A',
      invoiceDate = new Date().toLocaleDateString(),
      items = [],
      subtotal = 0,
      tax = 0,
      total = 0,
      businessName = "Lefty's Liquor",
      isVoided = false
    } = invoiceData;

    doc.setFontSize(14);
    doc.setTextColor(239, 68, 68);
    doc.text('VOIDED RECEIPT', 40, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text(businessName, 40, 30, { align: 'center' });
    
    doc.setFontSize(8);
    doc.text(`Inv: ${invoiceNumber}`, 10, 40);
    doc.text(`Date: ${invoiceDate}`, 10, 45);

    autoTable(doc, {
      startY: 50,
      head: [['Item', 'Qty', 'Total']],
      body: items.map(i => [i.name, i.quantity, i.total]),
      theme: 'plain',
      styles: { fontSize: 7 },
      margin: { left: 5, right: 5 }
    });

    let fY = doc.lastAutoTable.finalY + 5;
    doc.text(`Total: $${parseFloat(total).toFixed(2)}`, 75, fY, { align: 'right' });
  }



  /**
   * Generate and download PDF
   */
  downloadInvoicePDF(invoiceData) {
    const doc = this.generateInvoicePDF(invoiceData);
    doc.save(`Invoice_${invoiceData.invoiceNumber}.pdf`);
  }

  /**
   * Generate and trigger print dialog using a hidden iframe (same tab)
   */
  printInvoicePDF(invoiceData) {
    const doc = this.generateInvoicePDF(invoiceData);
    const blobUrl = doc.output('bloburl');
    
    // Create a hidden iframe
    const iframeId = 'pos-print-iframe';
    let iframe = document.getElementById(iframeId);
    if (iframe) document.body.removeChild(iframe);
    
    iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.style.display = 'none';
    iframe.src = blobUrl;
    
    document.body.appendChild(iframe);
    
    // Wait for PDF to load and trigger print
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 300);
    };
  }

  /**
   * Generate PDF blob for upload
   */
  generateInvoiceBlob(invoiceData) {
    const doc = this.generateInvoicePDF(invoiceData);
    return doc.output('blob');
  }

  /**
   * Generate PDF base64 string
   */
  generateInvoiceBase64(invoiceData) {
    const doc = this.generateInvoicePDF(invoiceData);
    return doc.output('dataurlstring');
  }

  /**
   * Upload invoice PDF to server and get URL
   * @param {Object} invoiceData - Invoice details
   * @param {Object} apiService - API service instance
   * @returns {Promise<string>} - Invoice URL
   */
  async uploadAndGetInvoiceURL(invoiceData, apiService) {
    try {
      const blob = this.generateInvoiceBlob(invoiceData);
      const fileName = `invoice_${invoiceData.invoiceNumber}_${Date.now()}.pdf`;
      
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', blob, fileName);
      formData.append('type', 'invoice');
      formData.append('invoice_number', invoiceData.invoiceNumber);

      // Upload to server (you'll need to implement this endpoint)
      // const response = await apiService.uploadInvoice(formData);
      // return response.data.url;

      // For now, return a mock URL (replace with actual upload logic)
      const mockUrl = `${window.location.origin}/invoices/${fileName}`;
      
      // Store blob in sessionStorage as fallback
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        sessionStorage.setItem(`invoice_${invoiceData.invoiceNumber}`, reader.result);
      };

      return mockUrl;
    } catch (error) {
      // console.error('Error uploading invoice:', error);
      throw error;
    }
  }

  /**
   * Generate shareable invoice link
   */
  generateShareableLink(invoiceNumber) {
    const base = process.env.REACT_APP_INVOICE_BASE_URL || 'https://et-pos.store';
    return `${base}/invoice/${invoiceNumber}`;
  }
}

export default new InvoiceGenerator();
